const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

interface AuthEndpoints {
  login: string;
  register: string;
  refresh: string;
  logout: string;
  me: string;
  users: string;
  userById: (id: string | number) => string;
  drivers: string;
  uploadProfileImage: (id: string | number) => string;
  assignVendor: string;
  getUserWallet: (id: string | number) => string;
  userOrders: (id: string | number) => string;
  codBan: (id: string | number) => string;
  codBanDevices: string;
  codBanDeviceById: (deviceId: string) => string;
}

interface StoreEndpoints {
  getAll: string;
  getById: (id: number) => string;
  create: string;
  update: (id: number) => string;
}

interface VendorEndpoints {
  getAll: string;
  getById: (id: number) => string;
  getOrders: (id: number) => string;
  exportOrders: (id: number) => string;
  getWorkingHours: (id: number) => string;
  updateWorkingHours: (id: number) => string;
}

interface OrderEndpoints {
  getAll: string;
  getById: (id: number) => string;
  create: string;
  updateStatus: (id: number) => string;
  refund: (id: number) => string;
  getAdminOrderDetails: (id: number) => string;
  getByStatus: (status: string) => string;
  assignToDriver: string;
  getLiveOrdersStats: string;
  exportOrders: string;
}

interface DriverEndpoints {
  getAllDrivers: string;
  getOnlineDrivers: string;
  getAvailableDrivers: string;
  getDriverLocation: (driverId: number) => string;
  getMultipleLocations: string;
  getEagleView: string;
  getDriverStatistics: (driverId: number) => string;
}

interface WalletEndpoints {
  getWallet: (walletId: number) => string;
  getTransactions: (walletId: number) => string;
  createTransaction: (walletId: number) => string;
  transferFunds: (walletId: number) => string;
  freezeWallet: (walletId: number) => string;
  unfreezeWallet: (walletId: number) => string;
  verifyTransaction: (transactionId: number) => string;
  processRemittance: (driverId: number) => string;
  remitCash: (driverId: number) => string;
  getCashAtHand: (driverId: number) => string;
  getRemittanceHistory: string;
  getAllTransactions: string;
  exportTransactions: string;
}

interface PayoutEndpoints {
  getVendorPaymentRequests: string;
  approvePaymentRequest: (id: string) => string;
  rejectPaymentRequest: (id: string) => string;
  processPayment: (id: string) => string;
  getVendorsWithBalance: string;
  payoutFullBalance: string;
}

interface DashboardEndpoints {
  getStats: string;
  updateTarget: string;
}

interface RatingEndpoints {
  userRatings: (userId: number) => string;
  userRating: (userId: number, id: number) => string;
  driverRatings: (driverId: number) => string;
  driverRating: (driverId: number, id: number) => string;
  vendorRatings: (vendorId: number) => string;
  vendorRating: (vendorId: number, id: number) => string;
}


interface ApiEndpoints {
  auth: AuthEndpoints;
  stores: StoreEndpoints;
  vendors: VendorEndpoints;
  orders: OrderEndpoints;
  drivers: DriverEndpoints;
  wallet: WalletEndpoints;
  payouts: PayoutEndpoints;
  dashboard: DashboardEndpoints;
  ratings: RatingEndpoints;
}

const API_ENDPOINTS: ApiEndpoints = {
  auth: {
    login: 'api/auth/login',
    register: "api/auth/register",
    refresh: "api/auth/refresh",
    logout: "api/auth/logout",
    me: "api/auth/me",
    users: '/api/admin/users',
    userById: (id) => `/api/admin/users/${id}`,
    drivers: '/api/admin/users/drivers',
    uploadProfileImage: (id) => `/api/admin/users/${id}/profile-image`,
    assignVendor: '/api/admin/users/assign-vendor',
    getUserWallet: (id) => `/api/admin/users/${id}/wallet`,
    userOrders: (id) => `/api/admin/users/${id}/orders`,
    codBan: (id) => `/api/admin/users/${id}/cod-ban`,
    codBanDevices: '/api/admin/cod-bans/devices',
    codBanDeviceById: (deviceId) => `/api/admin/cod-bans/devices/${deviceId}`,
  },
  stores: {
    getAll: 'api/stores',
    getById: (id) => `/api/stores/${id}`,
    create: 'api/admin/users/create-vendor',
    update: (id) => `/api/stores/${id}`,
  },
  vendors: {
    getAll: '/api/admin/vendors',
    getById: (id) => `/api/admin/vendors/${id}`,
    getOrders: (id) => `/api/admin/vendors/${id}/orders`,
    exportOrders: (id) => `/api/admin/vendors/${id}/orders/export`,
    getWorkingHours: (id) => `/api/admin/vendors/${id}/working-hours`,
    updateWorkingHours: (id) => `/api/admin/vendors/${id}/working-hours`,
  },
  orders: {
    getAll: '/api/orders',
    getById: (id) => `/api/orders/${id}`,
    create: '/api/orders',
    updateStatus: (id) => `/api/orders/${id}/status`,
    refund: (id) => `/api/orders/${id}/refund`,
    getAdminOrderDetails: (id) => `/api/admin/orders/${id}`,
    getByStatus: (status) => `/api/admin/orders/status/${status}`,
    assignToDriver: '/api/admin/orders/assign',
    getLiveOrdersStats: '/api/admin/live-orders/stats',
    exportOrders: '/api/admin/orders/export',
  },
  drivers: {
    getAllDrivers: '/api/driver-status/eagle-view',
    getOnlineDrivers: '/api/driver-status/online',
    getAvailableDrivers: '/api/driver-status/available',
    getDriverLocation: (driverId) => `/api/driver-status/${driverId}/location`,
    getMultipleLocations: '/api/driver-status/locations/bulk',
    getEagleView: '/api/driver-status/eagle-view',
    getDriverStatistics: (driverId) => `/api/admin/users/drivers/${driverId}/statistics`,
  },
  wallet: {
    getWallet: (walletId) => `/api/admin/wallets/${walletId}`,
    getTransactions: (walletId) => `/api/admin/wallets/${walletId}/transactions`,
    createTransaction: (walletId) => `/api/admin/wallets/${walletId}/transactions`,
    transferFunds: (walletId) => `/api/admin/wallets/${walletId}/transfer`,
    freezeWallet: (walletId) => `/api/admin/wallets/${walletId}/freeze`,
    unfreezeWallet: (walletId) => `/api/admin/wallets/${walletId}/unfreeze`,
    verifyTransaction: (transactionId) => `/api/admin/wallets/verify-transaction/${transactionId}`,
    processRemittance: (driverId) => `/api/wallets/driver/${driverId}/remittance`,
    remitCash: (driverId) => `/api/wallets/driver/${driverId}/remit-cash`,
    getCashAtHand: (driverId) => `/api/wallets/driver/${driverId}/cash-at-hand`,
    getRemittanceHistory: '/api/admin/users/drivers/remittance-history',
    getAllTransactions: '/api/admin/wallets/transactions',
    exportTransactions: '/api/admin/wallets/transactions/export',
  },
  payouts: {
    getVendorPaymentRequests: '/api/admin/vendor-payments',
    approvePaymentRequest: (id: string) => `/api/admin/vendor-payments/${id}/approve`,
    rejectPaymentRequest: (id: string) => `/api/admin/vendor-payments/${id}/reject`,
    processPayment: (id: string) => `/api/admin/vendor-payments/${id}/pay`,
    getVendorsWithBalance: '/api/admin/vendor-payments/vendors-with-balance',
    payoutFullBalance: '/api/admin/vendor-payments/payout-full-balance',
  },
  dashboard: {
    getStats: '/api/admin/dashboard/stats',
    updateTarget: '/api/admin/dashboard/target',
  },
  ratings: {
    userRatings: (userId) => `/api/admin/ratings/user/${userId}`,
    userRating: (userId, id) => `/api/admin/ratings/user/${userId}/${id}`,
    driverRatings: (driverId) => `/api/admin/ratings/driver/${driverId}`,
    driverRating: (driverId, id) => `/api/admin/ratings/driver/${driverId}/${id}`,
    vendorRatings: (vendorId) => `/api/admin/ratings/vendor/${vendorId}`,
    vendorRating: (vendorId, id) => `/api/admin/ratings/vendor/${vendorId}/${id}`,
  },

};

export { API_BASE_URL, API_ENDPOINTS };